/**
 * WebApp Version Management
 * 
 * Single source of truth for webapp version.
 * Used by: app_min1.html, delivery.html, delivery3.html, branches.html, start_day.html
 * 
 * To update version:
 * 1. Run: ./scripts/update-version.sh X.Y.Z
 * 2. Or ask Copilot to update version (requires user approval)
 * 
 * @version 1.9.0
 */

export const VERSION = '1.9.0';

/**
 * Initialize version display
 * - Updates document title with version
 * - Updates version badge element
 */
export function initVersion() {
  // Update title
  const baseTitle = document.title.replace(/\s*V[\d.]+\s*$/, '').trim();
  document.title = `${baseTitle} V${VERSION}`;
  
  // Update version badge
  const badge = document.getElementById('ver');
  if (badge) {
    badge.textContent = `V${VERSION}`;
  }
}

/**
 * Force update: clear all SW caches, unregister SWs, hard reload.
 */
export async function forceUpdateApp() {
  try {
    // 1. Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
    }
    // 2. Clear all caches
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k)));
    }
    // 3. Clear version from localStorage
    try {
      const key = 'webapp.courierSettings.v1';
      const stored = localStorage.getItem(key);
      if (stored) {
        const s = JSON.parse(stored);
        delete s.checkedVersion;
        localStorage.setItem(key, JSON.stringify(s));
      }
    } catch (_) {}
    // 4. Hard reload (bypass browser cache)
    window.location.reload(true);
  } catch (e) {
    console.error('[Version] Force update failed:', e);
    window.location.reload(true);
  }
}

/**
 * Check server version and auto-update if current page is outdated.
 * Returns { needsUpdate, serverVersion } or null on error.
 */
export async function checkVersionFromServer() {
  try {
    const resp = await fetch('/webapp/version', { cache: 'no-store' });
    if (!resp.ok) return null;
    const data = await resp.json();
    const serverVersion = data.version;
    if (!serverVersion) return null;
    const needsUpdate = serverVersion !== VERSION;
    return { needsUpdate, serverVersion, currentVersion: VERSION };
  } catch (_) {
    return null;
  }
}

// Make functions globally available for non-module scripts
if (typeof window !== 'undefined') {
  window.__appVersion = VERSION;
  window.__forceUpdateApp = forceUpdateApp;
  window.__checkVersionFromServer = checkVersionFromServer;
}

// Auto-init when DOM is ready
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVersion);
  } else {
    initVersion();
  }
}
